import { z } from 'zod';
/**
 * Rich-text bodies are stored as a ProseMirror/TipTap document (JSON), not as
 * an HTML string.
 *
 * Why JSON is the source of truth:
 *  - HTML from a browser-side editor is unpredictable; a structured document is
 *    not, so it can be re-rendered, migrated or transformed reliably.
 *  - It makes the sanitisation boundary explicit. The server renders JSON to
 *    HTML and sanitises the result once, on write. Nothing user-authored is
 *    ever concatenated straight into a response.
 *  - Plain-text extraction for search indexing and reading-time estimates is
 *    trivial from a tree and error-prone from a tag soup.
 *
 * The rendered HTML is cached alongside it in `bodyHtml` so the public read path
 * does no rendering work per request.
 *
 * Validation is intentionally shallow: this asserts the envelope is a document,
 * not that every node type is one we recognise. Deep validation would have to be
 * kept in lockstep with the editor's extension list, and the real safety net is
 * server-side sanitisation on render, not schema validation on input.
 */
export const richTextDocSchema = z
    .record(z.string(), z.unknown())
    .refine((value) => value['type'] === 'doc', {
    error: 'Body must be a ProseMirror document with type "doc".',
})
    .refine((value) => Array.isArray(value['content']), {
    error: 'Body document must contain a "content" array.',
});
/** An empty document — what a brand-new draft starts from. */
export const EMPTY_RICH_TEXT_DOC = {
    type: 'doc',
    content: [{ type: 'paragraph' }],
};
/**
 * HTML tags the sanitiser permits in rendered output.
 *
 * Kept here, in shared, because the editor's toolbar and the server's
 * sanitiser must agree. If they drift, authors get content silently stripped
 * after saving — one of the more infuriating CMS bugs to diagnose.
 */
export const ALLOWED_HTML_TAGS = [
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'code',
    'pre',
    'blockquote',
    'h2',
    'h3',
    'h4',
    'ul',
    'ol',
    'li',
    'a',
    'img',
    'figure',
    'figcaption',
    'hr',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
];
export const ALLOWED_HTML_ATTRIBUTES = {
    a: ['href', 'title', 'target', 'rel'],
    img: ['src', 'alt', 'width', 'height', 'loading', 'decoding'],
    th: ['colspan', 'rowspan', 'scope'],
    td: ['colspan', 'rowspan'],
    code: ['class'],
    pre: ['class'],
};
/** Only these URL schemes may appear in an href or src. */
export const ALLOWED_URL_SCHEMES = ['http', 'https', 'mailto', 'tel'];
/**
 * Walk a document and concatenate its text nodes.
 *
 * Used for reading-time estimation, meta-description fallbacks and search.
 * Iterative rather than recursive so a pathologically nested document — hand-
 * crafted or pasted from Word — cannot blow the stack.
 */
export function extractPlainText(doc) {
    const parts = [];
    const stack = [doc];
    while (stack.length > 0) {
        const node = stack.pop();
        if (node === null || typeof node !== 'object')
            continue;
        const record = node;
        if (typeof record['text'] === 'string') {
            parts.push(record['text']);
        }
        // Treat hard breaks and paragraph boundaries as whitespace so words from
        // adjacent blocks don't get glued together.
        if (record['type'] === 'paragraph' || record['type'] === 'hardBreak') {
            parts.push(' ');
        }
        const content = record['content'];
        if (Array.isArray(content)) {
            // Reverse-push keeps document order when popping off the end.
            for (let i = content.length - 1; i >= 0; i -= 1) {
                stack.push(content[i]);
            }
        }
    }
    return parts.join('').replace(/\s+/g, ' ').trim();
}
/** Average adult silent-reading speed for prose, rounded to a floor of 1. */
const WORDS_PER_MINUTE = 225;
export function estimateReadingMinutes(doc) {
    const text = extractPlainText(doc);
    if (text.length === 0)
        return 1;
    const words = text.split(' ').filter(Boolean).length;
    return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
}
//# sourceMappingURL=rich-text.js.map